import { AxiosInstance, AxiosRequestConfig } from "axios";

// The `meta` both apps' request interceptors read: statuses listed here are
// answers the module acts on, not errors to log.
interface DialBlockRequestMetaI {
  meta?: { expectedStatuses?: number[] };
}

/**
 * Dial blocks a workspace admin (LGA) manages for the callers on their
 * workspace's campaigns (backend GLE-8273, behind the `dial_blocks_enabled`
 * kill switch: every route answers 404 while it is off). Non-admin workspace
 * members get 401. Times are RFC 3339 in UTC; a week is the ET week that
 * starts on Monday 00:00 ET.
 */

export const DIAL_BLOCK_STATUSES = {
  SCHEDULED: "scheduled",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  NO_SHOW: "no_show",
} as const;

export type DialBlockStatusI =
  (typeof DIAL_BLOCK_STATUSES)[keyof typeof DIAL_BLOCK_STATUSES];

// A graded block's outcome (B4, Command Center's rule without dial time).
export const DIAL_BLOCK_OUTCOMES = {
  POWER: "power",
  WIN: "win",
  EFFORT: "effort",
  FUMBLE: "fumble",
} as const;

export type DialBlockOutcomeI =
  (typeof DIAL_BLOCK_OUTCOMES)[keyof typeof DIAL_BLOCK_OUTCOMES];

/**
 * A block's numbers, counted by the platform from its own calls about 15
 * minutes after the block ends (B4). They can still move until `final`,
 * 24 hours after the end. `meetings` are the meetings the caller booked
 * during the block, not the leaderboard's meeting count (B4-D73).
 */
export interface DialBlockMetricsI {
  dials: number;
  pitches: number;
  meetings: number;
  // The standards the block was graded against.
  standard_dials: number;
  standard_pitches: number;
  standard_meetings: number;
  computed_at: string;
  final: boolean;
}

export interface TeamDialBlockI {
  id: string;
  user_id: string;
  caller_first_name: string;
  caller_last_name: string;
  campaign_id: string;
  campaign_name: string;
  start_time: string;
  end_time: string;
  status: DialBlockStatusI;
  // Null until the block is graded, and always null for a no-show.
  outcome?: DialBlockOutcomeI | null;
  // Null until the block is first graded; absent on a backend before B4.
  metrics?: DialBlockMetricsI | null;
  // Only on the user app's "All workspaces" read (C-14,
  // GET /user/v1/manage/dial_blocks): the block's workspace.
  workspace_id?: string;
  workspace_name?: string | null;
}

// A caller on the workspace's roster, with or without a block that week.
export interface DialBlockCallerI {
  user_id: string;
  first_name: string;
  last_name: string;
  // The workspace's live campaigns this caller is approved on and not blocked
  // on (B2b). Absent on a backend that predates it.
  campaign_ids?: string[];
  // The caller's default weekly commitment (10 unless an LGA set 1 to 24) and
  // this week's: an approved availability request's number, else the default
  // (B2c). Absent on a backend that predates it.
  blocks_per_week?: number;
  week_commitment?: number;
  // Only on the "All workspaces" read (C-14).
  workspace_id?: string;
}

/**
 * One of the workspace's campaigns on the team week (B2c): every non-deleted
 * campaign of the workspace, whoever the LGA is linked to, sorted by name.
 */
export interface TeamWeekCampaignI {
  campaign_id: string;
  campaign_name: string | null;
  weekly_capacity: number | null;
  booked: number;
  left: number | null;
  dialing_start_hour_et: number;
  dialing_end_hour_et: number;
  // ISO weekdays the campaign's blocks may fall on, Mon=1..Sat=6 (7 =
  // Sunday, only once its LGA turned Sunday blocks on, C-16 S8).
  call_days: number[];
  // The campaign's blocks are paused (C-16 S8): nothing can be booked into it.
  // Absent on a backend without the controls.
  blocks_paused?: boolean;
  // The archive signals the customer app reads (helpers/campaign-archived).
  is_campaign_visible: boolean;
  has_published_campaign: boolean;
  admin_toggle_visibility: boolean;
  // Only on the "All workspaces" read (C-14).
  workspace_id?: string;
}

export interface GetWorkspaceDialBlocksParamsI {
  // Monday of the ET week, YYYY-MM-DD. The current week when omitted.
  week_start?: string;
}

export interface GetWorkspaceDialBlocksResponseI {
  week_start: string;
  callers: DialBlockCallerI[];
  dial_blocks: TeamDialBlockI[];
  // Absent on a backend that predates B2c: the calendar then falls back to
  // the Campaigns tab's list.
  campaigns?: TeamWeekCampaignI[];
}

export interface BookWorkspaceDialBlockParamsI {
  user_id: string;
  campaign_id: string;
  start_time: string;
}

// Send a new start, a new campaign, or both; at least one is required.
export interface MoveWorkspaceDialBlockParamsI {
  start_time?: string;
  campaign_id?: string;
}

export interface WorkspaceDialBlockResponseI {
  dial_block: TeamDialBlockI;
}

/**
 * A campaign's weekly dial block capacity (B2b, GLE-8277). The effective
 * capacity of a week is its own value, else the campaign's default, else 10
 * (Command Center's default); 0 means closed.
 */
export interface DialBlockCapacityWeekI {
  // Monday of the ET week, YYYY-MM-DD.
  week_start: string;
  weekly_capacity: number;
  // The week has its own value rather than the default.
  is_override: boolean;
  booked: number;
  // weekly_capacity - booked, never below 0.
  left: number;
}

export interface DialBlockCapacityI {
  campaign_id: string;
  campaign_name: string;
  default_weekly_capacity: number;
  weeks: DialBlockCapacityWeekI[];
}

export interface GetDialBlockCapacityParamsI {
  // Monday of the first week (ET); the current week when omitted.
  week_start?: string;
  // 1 to 12, default 4.
  weeks?: number;
}

export interface GetDialBlockCapacityResponseI {
  capacity: DialBlockCapacityI;
}

/**
 * Without `week_start` it sets the default; with it, that week's own value.
 * Send exactly one of `weekly_capacity` (0 to 1,000) and `clear` (the week
 * falls back to the default; the default goes back to 10). With `preview`
 * nothing changes and `cancelled_dial_blocks` lists what the change would
 * cancel: lowering a week below what is booked cancels its newest scheduled,
 * not-started blocks (Omar's D20 = A).
 */
export interface SetDialBlockCapacityParamsI {
  week_start?: string;
  weekly_capacity?: number;
  clear?: boolean;
  preview?: boolean;
}

export interface SetDialBlockCapacityResponseI {
  capacity: DialBlockCapacityI;
  cancelled_dial_blocks: TeamDialBlockI[];
  preview: boolean;
}

// A caller approved on the campaign, with their weekly cap on it.
export interface DialBlockCallerCapI {
  user_id: string;
  first_name: string;
  last_name: string;
  // Null when the caller has no cap.
  weekly_cap: number | null;
  // Blocks they hold on the campaign in the ET week asked for.
  booked: number;
}

export interface GetDialBlockCallerCapsResponseI {
  campaign_id: string;
  week_start: string;
  callers: DialBlockCallerCapI[];
}

// Exactly one of a cap (1 to 100) and `clear` (no cap).
export interface SetDialBlockCallerCapParamsI {
  weekly_cap?: number;
  clear?: boolean;
}

export interface SetDialBlockCallerCapResponseI {
  caller: DialBlockCallerCapI;
}

export const DIAL_BLOCK_AVAILABILITY_STATUSES = {
  PENDING: "pending",
  APPROVED: "approved",
  DENIED: "denied",
} as const;

export type DialBlockAvailabilityStatusI =
  (typeof DIAL_BLOCK_AVAILABILITY_STATUSES)[keyof typeof DIAL_BLOCK_AVAILABILITY_STATUSES];

export const DIAL_BLOCK_AVAILABILITY_REASONS = {
  SCHEDULED_CONFLICT: "scheduled_conflict",
  FAMILY_EMERGENCY: "family_emergency",
  ILLNESS: "illness",
  TRAVEL: "travel",
  OTHER: "other",
} as const;

/**
 * A caller's request to commit to fewer blocks one week (B2c), as the LGA
 * sees it. A pending request belongs to the caller's current roster; a
 * reviewed one stays with the workspace that reviewed it.
 */
export interface DialBlockAvailabilityRequestI {
  id: string;
  user_id: string;
  caller_first_name: string;
  caller_last_name: string;
  // Monday of the ET week, YYYY-MM-DD.
  week_start: string;
  reason_category: string;
  reason_notes: string | null;
  // The week's commitment if approved (absolute).
  reduced_blocks: number;
  // The caller's usual commitment that week.
  blocks_per_week: number;
  status: DialBlockAvailabilityStatusI;
  review_notes: string | null;
  // Null while pending.
  reviewer_name: string | null;
  reviewed_at: string | null;
  created_at: string;
  // --- C-12 wave 2, slice B: repeat requests (absent before its backend) ---
  // The week's earlier approved number, in force while this later request is
  // pending or after it was denied; null when none.
  standing_reduced_blocks?: number | null;
  // Decisions on earlier requests about the same week, newest first.
  earlier?: DialBlockAvailabilityDecisionI[];
  // --- end C-12 B ---
}

// --- C-12 wave 2, slice B: earlier decisions and usual commitment requests ---
/** An earlier reviewed request about a week, replaced by a later one. */
export interface DialBlockAvailabilityDecisionI {
  status: DialBlockAvailabilityStatusI;
  reason_category: string;
  reason_notes: string | null;
  reduced_blocks: number;
  review_notes: string | null;
  reviewer_name: string | null;
  reviewed_at: string;
  requested_at: string;
}

/**
 * A caller's request to change their usual weekly commitment (the user
 * app's manage zone only). Approving it starts the number the ET week after.
 */
export interface DialBlockUsualCommitmentRequestI {
  id: string;
  kind: "usual";
  user_id: string;
  caller_first_name: string | null;
  caller_last_name: string | null;
  // The usual commitment asked for, 1 to 24.
  blocks_per_week: number;
  // The caller's usual commitment for next week as it stands now.
  current_blocks_per_week: number;
  reason_notes: string | null;
  status: DialBlockAvailabilityStatusI;
  // The ET Monday an approved request starts on; null unless approved.
  effective_week_start: string | null;
  review_notes: string | null;
  reviewer_name: string | null;
  reviewed_at: string | null;
  created_at: string;
}

export interface GetDialBlockUsualCommitmentRequestsResponseI {
  requests: DialBlockUsualCommitmentRequestI[];
}

export interface DialBlockUsualCommitmentRequestResponseI {
  request: DialBlockUsualCommitmentRequestI;
}
// --- end C-12 B ---

export interface GetDialBlockAvailabilityRequestsResponseI {
  // Newest first (as Command Center lists them).
  requests: DialBlockAvailabilityRequestI[];
}

export interface ReviewDialBlockAvailabilityRequestParamsI {
  decision: "approve" | "deny";
  // Required, up to 2,000 characters.
  review_notes: string;
}

export interface DialBlockAvailabilityRequestResponseI {
  request: DialBlockAvailabilityRequestI;
}

export interface SetDialBlockCommitmentResponseI {
  caller: { user_id: string; blocks_per_week: number };
}

/**
 * A caller's dependability score (B6, Command Center's weekly RUI score):
 * 0 to 100, its letter (A from 90, B from 80, C from 70, else D) and the
 * last week's direction.
 */
export const DIAL_BLOCK_SCORE_TRENDS = {
  UP: "up",
  DOWN: "down",
  STABLE: "stable",
} as const;

export type DialBlockScoreTrendI =
  (typeof DIAL_BLOCK_SCORE_TRENDS)[keyof typeof DIAL_BLOCK_SCORE_TRENDS];

export interface DialBlockScoreI {
  score: number;
  letter_grade: string;
  trend: DialBlockScoreTrendI;
}

/**
 * One roster caller's health inputs (R1, from B6): blocks that fumbled on
 * the workspace's campaigns, ever (not cancelled), and the score, null until
 * the caller is first scored.
 */
export interface DialBlockRepHealthCallerI {
  user_id: string;
  lifetime_fumbles: number;
  score: DialBlockScoreI | null;
  // Live strikes on the workspace's campaigns (C-14 phase 2, W3); absent
  // while strikes are off.
  live_strikes?: number;
  // Only on the Admin's "All workspaces" read (C-16 B-1,
  // GET /user/v1/manage/dial_block_rep_health): the caller's roster
  // workspace. A caller on two workspaces is listed once per workspace.
  workspace_id?: string;
  workspace_name?: string | null;
}

/**
 * A strike (C-14 phase 2, W3; Omar's rules S1-S7): a final block graded
 * fumble, or a no-show with no dials, counting for 30 days unless an LGA or
 * Admin excused it. Three live strikes are a flag (C14 D67): nothing pauses.
 */
export interface DialBlockStrikeI {
  dial_block_id: string;
  campaign_id: string;
  campaign_name: string;
  start_time: string;
  kind: "fumble" | "no_show";
  expires_at: string;
  excused: boolean;
  excused_by_name: string | null;
  excuse_reason: string | null;
  excused_at: string | null;
}

export interface GetDialBlockRepHealthResponseI {
  callers: DialBlockRepHealthCallerI[];
}

/**
 * A campaign's month on Campaign Health (R2, B2b): meetings by the platform's
 * /leaderboard math (booked by booking date; qualified, show and fumbled
 * cards by meeting date) and the block dials and pitches of its completed
 * blocks, by ET month.
 */
export interface CampaignHealthMonthI {
  // YYYY-MM, ET.
  month: string;
  booked_meetings: number;
  qualified_meetings: number;
  show_meetings: number;
  fumbled_meetings: number;
  block_dials: number;
  block_pitches: number;
}

// Command Center's weekly Performance Rates inputs, per ET week.
export interface CampaignHealthWeekI {
  week_start: string;
  // Blocks that ended more than 24 hours ago and weren't cancelled.
  eligible_blocks: number;
  dials: number;
  pitches: number;
  // B4's meetings booked in the blocks.
  block_meetings: number;
}

export interface CampaignHealthMetricsI {
  campaign_id: string;
  campaign_name: string | null;
  // 4: the 3 previous ET months, then the current one.
  months: CampaignHealthMonthI[];
  // Since 2025-01-01.
  lifetime: {
    booked_meetings: number;
    qualified_meetings: number;
    show_meetings: number;
    fumbled_meetings: number;
  };
  // 9: the 8 previous ET weeks, then the current one.
  weeks: CampaignHealthWeekI[];
  // Only on the Admin's "All workspaces" read (C-16 B-1,
  // GET /user/v1/manage/dial_block_campaign_health): the campaign's workspace.
  workspace_id?: string;
  workspace_name?: string | null;
}

export interface GetDialBlockCampaignHealthResponseI {
  campaigns: CampaignHealthMetricsI[];
}

/**
 * One scored ET week of a caller (B6, Command Center's weekly score row,
 * CC's column names), newest first in a score detail.
 */
export interface DialBlockScoreWeekI {
  week_start: string;
  required_blocks: number;
  blocks_scheduled: number;
  blocks_completed: number;
  remaining_blocks: number;
  extra_blocks_count: number;
  trouble_blocks_count: number;
  on_time_blocks_count: number;
  streak_consecutive_blocks: number;
  base_score: number;
  total_block_base_points: number;
  total_metric_bonuses: number;
  total_timeliness_bonuses: number;
  total_streak_multiplier_effect: number;
  weekly_completion_bonus: number;
  remaining_penalty: number;
  zero_metric_penalty: number;
  net_weekly_change: number;
  // An LGA's manual adjustments that week (applied after the weekly cap).
  adjustment_points: number;
  weekly_score: number;
  is_grace: boolean;
  computed_at: string;
  final: boolean;
}

export interface DialBlockScoreAdjustmentI {
  id: string;
  week_start: string;
  adjustment_points: number;
  reason: string;
  adjusted_by_name: string | null;
  created_at: string;
}

/**
 * A caller's score detail (R1b, B6): the current score (null until first
 * scored), the last 12 weeks and the last 20 adjustments, limited to this
 * workspace.
 */
export interface GetDialBlockScoreResponseI {
  score: (DialBlockScoreI & { week_start: string; is_grace: boolean }) | null;
  weeks: DialBlockScoreWeekI[];
  adjustments: DialBlockScoreAdjustmentI[];
}

export interface AdjustDialBlockScoreParamsI {
  // Not 0, up to 2 decimals; the new score must stay within 0 to 100.
  adjustment_points: number;
  // 1 to 2,000 characters.
  reason: string;
}

export interface AdjustDialBlockScoreResponseI {
  adjustment: DialBlockScoreAdjustmentI;
  score: GetDialBlockScoreResponseI["score"];
}

/**
 * One ET day of a caller's completed blocks (R1b(c), from B6): every day
 * zero-filled, oldest first, the last one today. No dial time (B4-D74).
 */
export interface DialBlockProductivityDayI {
  date: string;
  blocks: number;
  dials: number;
  pitches: number;
  meetings: number;
}

export interface GetDialBlockProductivityResponseI {
  days: DialBlockProductivityDayI[];
}

// --- C-12 wave 2, slice A: score reset (the user app's manage zone only) ---
export interface ResetDialBlockScoreResponseI {
  // False when the caller was already at 85 (a repeat): nothing written.
  reset: boolean;
  // The "Score reset" adjustments: one, or two over 100 points.
  adjustments: DialBlockScoreAdjustmentI[];
  score: GetDialBlockScoreResponseI["score"];
}

export interface ResetDialBlockScoresResponseI {
  reset: number;
  already_at_starting: number;
  // Being scored at that moment; resetting again is safe.
  busy: number;
}
// --- end C-12 A ---

/**
 * The manager block routes under one base path: `/v1/workspace` in
 * apps/customer (the customer zone) and `/v1/manage/workspace` in apps/user
 * (the role zone, C-14). Both zones answer the same shapes.
 */
export const createDialBlockManageApi = (
  api: AxiosInstance,
  basePath: string,
  {
    allWorkspacesPath,
    allWorkspacesHealthPath,
  }: {
    // The Admin's read across every workspace (`/v1/manage/dial_blocks` in
    // apps/user). The customer app has none.
    allWorkspacesPath?: string;
    // The Admin's Health reads across every workspace (C-16 B-1, under
    // `/v1/manage` in apps/user). The customer app has none.
    allWorkspacesHealthPath?: string;
  } = {}
) => ({
  getAllDialBlocks: (params: GetWorkspaceDialBlocksParamsI) =>
    allWorkspacesPath
      ? api.get<GetWorkspaceDialBlocksResponseI>(allWorkspacesPath, {
          params,
          meta: { expectedStatuses: [404] },
        } as AxiosRequestConfig & DialBlockRequestMetaI)
      : Promise.reject(
          new Error("This app has no All workspaces read for dial blocks")
        ),

  getWorkspaceDialBlocks: (
    workspaceId: string,
    params: GetWorkspaceDialBlocksParamsI
  ) =>
    api.get<GetWorkspaceDialBlocksResponseI>(
      `${basePath}/${workspaceId}/dial_blocks`,
      // A 404 here is the switch being off (prod today): an answer the
      // calendar acts on, not an error to log on every admin page load.
      // Built per call, because the request interceptor writes into `meta`.
      {
        params,
        meta: { expectedStatuses: [404] },
      } as AxiosRequestConfig & DialBlockRequestMetaI
    ),

  bookWorkspaceDialBlock: (
    workspaceId: string,
    params: BookWorkspaceDialBlockParamsI
  ) =>
    api.post<WorkspaceDialBlockResponseI>(
      `${basePath}/${workspaceId}/dial_blocks`,
      params
    ),

  moveWorkspaceDialBlock: (
    workspaceId: string,
    dialBlockId: string,
    params: MoveWorkspaceDialBlockParamsI
  ) =>
    api.patch<WorkspaceDialBlockResponseI>(
      `${basePath}/${workspaceId}/dial_blocks/${dialBlockId}`,
      params
    ),

  cancelWorkspaceDialBlock: (workspaceId: string, dialBlockId: string) =>
    api.delete<WorkspaceDialBlockResponseI>(
      `${basePath}/${workspaceId}/dial_blocks/${dialBlockId}`
    ),

  getDialBlockCapacity: (
    workspaceId: string,
    campaignId: string,
    params: GetDialBlockCapacityParamsI
  ) =>
    api.get<GetDialBlockCapacityResponseI>(
      `${basePath}/${workspaceId}/campaign/${campaignId}/dial_block_capacity`,
      { params }
    ),

  setDialBlockCapacity: (
    workspaceId: string,
    campaignId: string,
    params: SetDialBlockCapacityParamsI
  ) =>
    api.put<SetDialBlockCapacityResponseI>(
      `${basePath}/${workspaceId}/campaign/${campaignId}/dial_block_capacity`,
      params
    ),

  getDialBlockCallerCaps: (
    workspaceId: string,
    campaignId: string,
    params: { week_start?: string }
  ) =>
    api.get<GetDialBlockCallerCapsResponseI>(
      `${basePath}/${workspaceId}/campaign/${campaignId}/dial_block_caps`,
      { params }
    ),

  setDialBlockCallerCap: (
    workspaceId: string,
    campaignId: string,
    userId: string,
    params: SetDialBlockCallerCapParamsI
  ) =>
    api.put<SetDialBlockCallerCapResponseI>(
      `${basePath}/${workspaceId}/campaign/${campaignId}/dial_block_caps/${userId}`,
      params
    ),

  // R1 (B6). A backend without it answers 404 (glen's unknown route), which
  // only means Rep Health isn't there yet: not an error to log.
  getDialBlockRepHealth: (workspaceId: string) =>
    api.get<GetDialBlockRepHealthResponseI>(
      `${basePath}/${workspaceId}/dial_block_rep_health`,
      {
        meta: { expectedStatuses: [404] },
      } as AxiosRequestConfig & DialBlockRequestMetaI
    ),

  // C-16 B-1: Rep Health over every workspace (Admin). Off, the backend
  // answers 404 + 1035.
  getAllDialBlockRepHealth: () =>
    allWorkspacesHealthPath
      ? api.get<GetDialBlockRepHealthResponseI>(
          `${allWorkspacesHealthPath}/dial_block_rep_health`,
          {
            meta: { expectedStatuses: [404] },
          } as AxiosRequestConfig & DialBlockRequestMetaI
        )
      : Promise.reject(
          new Error("This app has no All workspaces read for Rep Health")
        ),

  // C-16 B-1: Campaign Health over every workspace (Admin). Off, the backend
  // answers 404 + 1035.
  getAllDialBlockCampaignHealth: () =>
    allWorkspacesHealthPath
      ? api.get<GetDialBlockCampaignHealthResponseI>(
          `${allWorkspacesHealthPath}/dial_block_campaign_health`,
          {
            meta: { expectedStatuses: [404] },
          } as AxiosRequestConfig & DialBlockRequestMetaI
        )
      : Promise.reject(
          new Error("This app has no All workspaces read for Campaign Health")
        ),

  // R2 (B2b). A backend without it answers 404 (glen's unknown route), which
  // only means the month numbers aren't there yet: not an error to log.
  getDialBlockCampaignHealth: (workspaceId: string) =>
    api.get<GetDialBlockCampaignHealthResponseI>(
      `${basePath}/${workspaceId}/dial_block_campaign_health`,
      {
        meta: { expectedStatuses: [404] },
      } as AxiosRequestConfig & DialBlockRequestMetaI
    ),

  // R1b (B6). A backend without it answers 404 (glen's unknown route).
  getDialBlockScore: (workspaceId: string, userId: string) =>
    api.get<GetDialBlockScoreResponseI>(
      `${basePath}/${workspaceId}/callers/${userId}/dial_block_score`,
      {
        meta: { expectedStatuses: [404] },
      } as AxiosRequestConfig & DialBlockRequestMetaI
    ),

  adjustDialBlockScore: (
    workspaceId: string,
    userId: string,
    params: AdjustDialBlockScoreParamsI
  ) =>
    api.post<AdjustDialBlockScoreResponseI>(
      `${basePath}/${workspaceId}/callers/${userId}/dial_block_score_adjustments`,
      params
    ),

  // R1b(c) (B6): `days` 1–120 gives today and that many days back. A 404
  // before B6 deploys it is expected, like R1.
  getDialBlockProductivity: (
    workspaceId: string,
    userId: string,
    params: { days: number }
  ) =>
    api.get<GetDialBlockProductivityResponseI>(
      `${basePath}/${workspaceId}/callers/${userId}/dial_block_productivity`,
      {
        params,
        meta: { expectedStatuses: [404] },
      } as AxiosRequestConfig & DialBlockRequestMetaI
    ),

  getDialBlockAvailabilityRequests: (
    workspaceId: string,
    params: { status: "pending" | "reviewed" }
  ) =>
    api.get<GetDialBlockAvailabilityRequestsResponseI>(
      `${basePath}/${workspaceId}/dial_block_availability_requests`,
      { params }
    ),

  reviewDialBlockAvailabilityRequest: (
    workspaceId: string,
    requestId: string,
    params: ReviewDialBlockAvailabilityRequestParamsI
  ) =>
    api.post<DialBlockAvailabilityRequestResponseI>(
      `${basePath}/${workspaceId}/dial_block_availability_requests/${requestId}/review`,
      params
    ),

  // --- C-12 wave 2, slice B: usual commitment requests (the user app's
  // manage zone only; 404 + 1035 before the backend has them) ---
  getDialBlockUsualCommitmentRequests: (
    workspaceId: string,
    params: { status: "pending" | "reviewed" }
  ) =>
    api.get<GetDialBlockUsualCommitmentRequestsResponseI>(
      `${basePath}/${encodeURIComponent(workspaceId)}/dial_block_usual_commitment_requests`,
      {
        params,
        meta: { expectedStatuses: [404] },
      } as AxiosRequestConfig & DialBlockRequestMetaI
    ),

  reviewDialBlockUsualCommitmentRequest: (
    workspaceId: string,
    requestId: string,
    params: ReviewDialBlockAvailabilityRequestParamsI
  ) =>
    api.post<DialBlockUsualCommitmentRequestResponseI>(
      `${basePath}/${encodeURIComponent(workspaceId)}/dial_block_usual_commitment_requests/${encodeURIComponent(requestId)}/review`,
      params
    ),
  // --- end C-12 B ---

  setCallerDialBlockCommitment: (
    workspaceId: string,
    userId: string,
    params: { blocks_per_week: number }
  ) =>
    api.put<SetDialBlockCommitmentResponseI>(
      `${basePath}/${workspaceId}/callers/${userId}/dial_block_commitment`,
      params
    ),

  // C-14 phase 2, W3: a caller's strikes on the workspace (the user app's
  // manage zone only; 404 + 1035 while strikes are off).
  listCallerDialBlockStrikes: (workspaceId: string, userId: string) =>
    api.get<{ strikes: DialBlockStrikeI[] }>(
      `${basePath}/${encodeURIComponent(workspaceId)}/reps/${encodeURIComponent(userId)}/strikes`
    ),

  excuseCallerDialBlockStrike: (
    workspaceId: string,
    userId: string,
    dialBlockId: string,
    reason: string
  ) =>
    api.post<{ strike: DialBlockStrikeI }>(
      `${basePath}/${encodeURIComponent(workspaceId)}/reps/${encodeURIComponent(userId)}/strikes/${encodeURIComponent(dialBlockId)}/excuse`,
      { reason }
    ),

  // --- C-12 wave 2, slice A: score reset (manage zone only; 404 + 1035
  // before the backend has it) ---
  resetDialBlockScore: (workspaceId: string, userId: string) =>
    api.post<ResetDialBlockScoreResponseI>(
      `${basePath}/${encodeURIComponent(workspaceId)}/callers/${encodeURIComponent(userId)}/dial_block_score_reset`
    ),

  resetDialBlockScores: (workspaceId: string) =>
    api.post<ResetDialBlockScoresResponseI>(
      `${basePath}/${encodeURIComponent(workspaceId)}/dial_block_score_resets`
    ),
  // --- end C-12 A ---
});

export type DialBlockManageApiI = ReturnType<typeof createDialBlockManageApi>;
